import { useEffect, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { Play, Loader2 } from "lucide-react";

export default function SqlEditor() {
  const [conns, setConns] = useState([]);
  const [connId, setConnId] = useState("");
  const [sql, setSql] = useState("SELECT 1");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/connections").then((r) =>
      setConns((r.data.connections || []).filter((c) => c.source_type !== "demo"))
    );
  }, []);

  const run = async () => {
    setBusy(true);
    setErr("");
    setRes(null);

    try {
      const r = await api.post("/query/run", {
        connection_id: connId,
        sql,
        limit: 500,
      });
      setRes(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>SQL Editor</h1>
            <p>Run read-only SELECT queries against approved analytics database connections.</p>
          </div>

          <button
            className="erp-primary-btn"
            onClick={run}
            disabled={!connId || busy}
            data-testid="sql-run"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} strokeWidth={1.8} />}
            Run Query
          </button>
        </div>

        <div className="space-y-3">
          <div className="card-flat p-3 flex items-end gap-3" data-testid="sql-toolbar">
            <div className="flex-1">
              <label className="label">Connection</label>
              <select
                className="select"
                value={connId}
                onChange={(e) => setConnId(e.target.value)}
                data-testid="sql-conn"
              >
                <option value="">— pick connection —</option>
                {conns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.source_type})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <textarea
            className="input font-mono"
            rows="12"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            data-testid="sql-text"
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 13,
              background: "#0f172a",
              color: "#e2e8f0",
              borderColor: "#1e293b",
              minHeight: 260,
            }}
          />

          {err && (
            <div
              className="card-flat p-3 text-[12.5px]"
              style={{ color: "#991b1b", background: "#fef2f2", borderColor: "#fecaca" }}
              data-testid="sql-error"
            >
              {err}
            </div>
          )}

          {res && (
            <div className="card-flat p-3" data-testid="sql-result">
              <div className="text-[12px] mb-2" style={{ color: "var(--c-text-muted)" }}>
                {res.row_count} rows · {res.execution_ms} ms{res.truncated ? " · truncated" : ""}
              </div>

              <div className="overflow-auto max-h-[480px]">
                <table className="tbl">
                  <thead>
                    <tr>
                      {res.columns.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {res.rows.map((r, i) => (
                      <tr key={i}>
                        {r.map((v, j) => (
                          <td key={j} className={typeof v === "number" ? "num" : ""}>
                            {v == null ? "—" : String(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
